import { ErrorReporter } from '@wix/editor-error-reporter';
import type {
  CompRef,
  DocumentServicesObject,
  SerializedCompStructure,
} from '@wix/document-services-types';
import { type AnimationsKit } from '@wix/animations-kit';
import {
  PreviewerSupportedTriggers,
  type PreviewerEffectsMapping,
  type PreviewerViewportAnimationData,
  type ViewerDoneParams,
  type PinElementPositionConfig,
} from './types';
import type { FixedHeaderParam, PreviewParams } from './previewerApi';
import { getPreviewOldToNewIdsMap } from '@wix/editor-site-generator';

export const getPreviewerEffectsMapping = (
  miniDs: DocumentServicesObject,
  rootPointer: CompRef,
): PreviewerEffectsMapping => {
  const children = miniDs.components.getChildren(rootPointer, true);
  return children.reduce((acc, childRef) => {
    const effectRefs = miniDs.components.effects.list(childRef);
    if (effectRefs.length === 0) return acc;
    return {
      ...acc,
      [childRef.id]: effectRefs
        .map((effectRef) => ({
          effectId: effectRef.id,
          animationOptions: miniDs.components.effects.get(childRef, effectRef)
            ?.value,
        }))
        .filter((effectData) => effectData.animationOptions?.namedEffect),
    };
  }, {});
};

export const extractSelfViewportAnimationComponents = (
  triggersAndReactionsConfig: ViewerDoneParams['options']['triggersAndReactions'],
  effectsMapping: PreviewerEffectsMapping,
): { [compId: string]: PreviewerViewportAnimationData } => {
  const compIdToReactionData = Object.entries(
    triggersAndReactionsConfig.compsToTriggers,
  ).reduce(
    (acc, [triggerId, triggerTypeMap]) => {
      const effectId =
        triggerTypeMap[PreviewerSupportedTriggers.ViewportEnter]?.[
          triggerId
        ]?.[0]?.reactions?.[0]?.reactionData?.effect;
      if (effectId) {
        acc[triggerId] = effectId;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
  return Object.entries(compIdToReactionData).reduce(
    (acc, [compId, effectId]) => {
      if (!effectId || !effectsMapping[compId]) return acc;
      const effect = effectsMapping[compId].find(
        (effectData) => effectData.effectId === effectId,
      );
      if (!effect?.animationOptions?.namedEffect) return acc;
      const duration =
        typeof effect.animationOptions.duration === 'number'
          ? effect.animationOptions.duration
          : effect.animationOptions.duration.value;
      const { type, power, direction } = effect.animationOptions.namedEffect;
      return {
        ...acc,
        [compId]: {
          allowReplay: effect.animationOptions.allowReplay || '',
          duration: (duration || 0) / 1000,
          delay: (effect.animationOptions.delay || 0) / 1000,
          name: type,
          power,
          direction,
          effectId,
        },
      };
    },
    {},
  );
};

export const runPreviewAnimation = (
  animationsKit: AnimationsKit,
  shadowRoot: ShadowRoot,
  triggersAndReactionsConfig: ViewerDoneParams['options']['triggersAndReactions'],
  effectsMapping: PreviewerEffectsMapping,
) => {
  const selfViewportAnimationComponents =
    extractSelfViewportAnimationComponents(
      triggersAndReactionsConfig,
      effectsMapping,
    );

  const observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          const element = entry.target as HTMLElement;
          const { delay, duration, name, allowReplay, power, direction } =
            selfViewportAnimationComponents[element.id];
          try {
            animationsKit.animate(name, element, duration, delay, {
              allowReplay,
              power,
              direction,
            });
          } catch (e) {
            console.error(e);
            ErrorReporter.captureException(e, {
              tags: { previewerRunAnimationKit: true },
              extra: {
                name,
                delay,
                duration,
                allowReplay,
              },
            });
            element.style.opacity = '1';
          }
          observer.unobserve(element);
        }
      });
    },
    { rootMargin: '50% 0px', threshold: 0.15 },
  );
  Object.keys(selfViewportAnimationComponents).forEach((compId) => {
    const el = shadowRoot.getElementById(compId);
    if (el) {
      el.style.opacity = '0';
      observer.observe(el);
    }
  });
};

export function getDefaultBaseWidth(): number {
  return Math.max(
    window.document.body.getBoundingClientRect().width - 100,
    980,
  );
}

export const getPinElementsPreviewsStructure = (
  pinElements: SerializedCompStructure[],
  displayWidth: number,
): PreviewParams[] => {
  return pinElements.map((pinElementStructure) => {
    const baseWidth = getDefaultBaseWidth();

    const isPinElementRotated =
      pinElementStructure.layout.rotationInDegrees &&
      Math.abs(pinElementStructure.layout.rotationInDegrees) !== 180;

    const scale = displayWidth / baseWidth;

    const dockedRightValue = pinElementStructure.layout.docked?.right?.px;
    const dockedLeftValue = pinElementStructure.layout.docked?.left?.px;

    let x = pinElementStructure.layout.x;

    if (dockedRightValue !== undefined) {
      x =
        (displayWidth -
          pinElementStructure.layout.width * scale -
          dockedRightValue * scale) /
        scale;
    }

    if (dockedLeftValue !== undefined) {
      x = dockedLeftValue / scale;
    }

    if (baseWidth > 980) {
      x -= (baseWidth - 980) * 0.5;
    }

    const layout: SerializedCompStructure['layout'] = {
      ...pinElementStructure.layout,
      docked: undefined,
      x,
      y: 0,
      fixedPosition: false,
      height: isPinElementRotated
        ? Math.max(
            pinElementStructure.layout.width,
            pinElementStructure.layout.height,
          )
        : pinElementStructure.layout.height,
    };

    return {
      sourceTemplateId: '',
      previewData: {
        structure: {
          ...pinElementStructure,
          layout,
        },
        compsOldToNewIdsMap: getPreviewOldToNewIdsMap({
          ...pinElementStructure,
          layout,
        }),
        previewBackground: 'transparent',
      },
      title: '',
      pinElementConfig: {
        height: pinElementStructure.layout.height,
        width: pinElementStructure.layout.width,
        x: pinElementStructure.layout.x,
        y: pinElementStructure.layout.y,
        docked: pinElementStructure.layout.docked,
        rotationInDegrees: pinElementStructure.layout.rotationInDegrees,
      },
    };
  });
};

export const getFixedHeaderPreviewStructure = (
  header: FixedHeaderParam,
  isMobile: boolean,
): PreviewParams => {
  const { fixedHeaderPreset, previewIndicator } = header;
  const { sourceTemplateId, structure, mobileStructure, title } =
    fixedHeaderPreset;
  const headerStructure = isMobile ? mobileStructure : structure;

  return {
    sourceTemplateId,
    previewData: {
      structure: headerStructure,
      compsOldToNewIdsMap: getPreviewOldToNewIdsMap(headerStructure),
    },
    title,
    previewIndicator,
  };
};

export const getFixedHeaderStyle = (
  { height }: { height: number },
  previewConfig: {
    scale: number;
  },
) => {
  if (!height || !previewConfig) {
    return {};
  }

  return {
    height: height * previewConfig.scale,
  };
};

export const getPinElementStyle = (
  pinElementConfig: PinElementPositionConfig,
  previewConfig: {
    scale: number;
    previewHeight: number;
    visiblePreviewHeight: number;
    displayWidth: number;
  },
) => {
  const { previewHeight, visiblePreviewHeight, scale, displayWidth } =
    previewConfig;
  const dockedBottomValue = pinElementConfig.docked?.bottom?.px;
  const dockedTopValue = pinElementConfig.docked?.top?.px;

  let pinElementHeight = pinElementConfig.height;

  if ((pinElementConfig.height + pinElementConfig.y) * scale > previewHeight) {
    const dockedBottomOffsetValue = dockedBottomValue || 0;
    const dockedTopOffsetValue = dockedTopValue || 0;
    const scaledDockedOffsets =
      (dockedBottomOffsetValue + dockedTopOffsetValue) * scale;

    pinElementHeight = previewHeight - scaledDockedOffsets;
  }

  if (
    pinElementConfig.rotationInDegrees &&
    Math.abs(pinElementConfig.rotationInDegrees) !== 180
  ) {
    pinElementHeight = Math.max(
      pinElementConfig.width,
      pinElementConfig.height,
    );
  }

  let top = 0;

  if (dockedBottomValue !== undefined) {
    top = visiblePreviewHeight - (dockedBottomValue + pinElementHeight) * scale;
  }

  if (dockedTopValue !== undefined) {
    top = dockedTopValue * scale;
  }

  return {
    top,
    width: displayWidth,
    height: pinElementHeight * scale,
    marginTop: -(pinElementHeight * scale),
  };
};
